/**
 * Validate a redirect path to prevent open-redirect vulnerabilities.
 *
 * Rules:
 * - Only relative paths allowed (must start with /)
 * - Rejects absolute URLs (http://, https://)
 * - Rejects protocol-relative paths (//, /\)
 * - Blocks known-bad routes (login, error pages)
 *
 * Safe to use with Router state from location.state.from
 *
 * @param path - The path to validate
 * @returns The validated path, or null if invalid/blocked
 */
export const validateRedirectPath = (
  path: string | null | undefined,
): string | null => {
  if (!path) {
    return null;
  }

  // Reject absolute URLs
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return null;
  }

  // Reject protocol-relative paths (//evil.com, /\evil.com)
  if (path.startsWith("//") || path.includes("\\")) {
    return null;
  }

  // Must be a relative path starting with /
  if (!path.startsWith("/")) {
    return null;
  }

  // Block paths that should never be redirect destinations after login
  const blockedPaths = ["/login", "/404", "/no-access"];
  if (blockedPaths.some((blocked) => path.startsWith(blocked))) {
    return null;
  }

  // Path is valid
  return path;
};
